import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import type { ReactNode } from 'react';
import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard/Dashboard';
import Teknisi from './pages/Teknisi/Teknisi';
import GuestMonitoring from './pages/GuestMonitoring/GuestMonitoring';
import Cleaning from './pages/Cleaning/Cleaning';
import ACMonitoring from './pages/ACMonitoring/ACMonitoring';
import WasteManagement from './pages/WasteManagement/WasteManagement';
import Procurement from './pages/Procurement/Procurement';
import Laundry from './pages/Laundry/Laundry';
import ProjectManagement from './pages/ProjectManagement/ProjectManagement';
import RolePrototype from './pages/RolePrototype/RolePrototype';
import ProviderOpportunities from './pages/ProviderOpportunities/ProviderOpportunities';
import { ProviderFavoriteProvider } from './contexts/ProviderFavoriteContext';
import ProviderOpportunityDetail from './pages/ProviderOpportunityDetail/ProviderOpportunityDetail';
import ProviderBidCreate from './pages/ProviderBidCreate/ProviderBidCreate';
import ProviderBids from './pages/ProviderBids/ProviderBids';
import ProviderPesan from './pages/ProviderPesan/ProviderPesan';
import Notifikasi from './pages/Notifikasi/Notifikasi';
import FavoritProvider from './pages/Favorit/FavoritProvider';
import Login from './pages/Login/Login';
import Landing from './pages/Landing/Landing';
import SsoCallback from './pages/SsoCallback/SsoCallback';
import { AcceptInvitationPage } from './pages/AcceptInvitation/AcceptInvitationPage';
import OwnerLayout from './owner/layout/OwnerLayout';
import Beranda from './owner/pages/Beranda/Beranda';
import PasangProyek from './owner/pages/PasangProyek/PasangProyek';
import ProyekSaya from './owner/pages/ProyekSaya/ProyekSaya';
import ProyekSayaDetail from './owner/pages/ProyekSayaDetail/ProyekSayaDetail';
import PenawaranMasuk from './owner/pages/PenawaranMasuk/PenawaranMasuk';
import PenawaranDetail from './owner/pages/PenawaranDetail/PenawaranDetail';
import Favorit from './owner/pages/Favorit/Favorit';
import VendorTersimpan from './owner/pages/VendorTersimpan/VendorTersimpan';
import Pesan from './owner/pages/Pesan/Pesan';
import logoApp from './assets/images/logo.png';
import axios from './api/axios';
import {
  createPrototypeUserProfile,
  getPrototypeRoleByUserType,
} from './data/manproRolePrototype';
import { FacilityManagementProvider } from './features/facility/context/FacilityManagementContext';
import type {
  ApiResponseEnvelope,
  FacilitySubmodule,
  PortalRole,
  UserProfile,
} from './types/auth';


const INTERNAL_USER_TYPES = [
  'super-admin',
  'admin-employee',
  'provider-merchant',
  'provider-serviceman',
  'provider-architect',
] as const;


const OWNER_ROLES = ['project_owner'];
const FACILITY_ROLES = ['provider', ...INTERNAL_USER_TYPES];

/** Zoom 100% hanya di landing (guest `/`); halaman lain tetap 80%. */
function AppZoomShell({
  children,
  isAuthenticated,
}: {
  children: ReactNode;
  isAuthenticated: boolean;
}) {
  const { pathname } = useLocation();
  const isLandingPage = !isAuthenticated && pathname === '/';

  return (
    <div className="min-h-screen" style={{ zoom: isLandingPage ? '100%' : '80%' }}>
      {children}
    </div>
  );
}

const normalizePortalRole = (userType?: string): PortalRole => {
  if (
    userType === 'customer' ||
    userType === 'corporate-client' ||
    userType === 'bumn-private'
  ) {
    return 'project_owner';
  }

  if (userType?.includes('provider')) {
    return 'provider';
  }

  return '';
};

const parseStoredPermissions = (): string[] => {
  const rawPermissions = localStorage.getItem('userRoles');

  if (!rawPermissions) {
    return [];
  }

  try {
    const parsed = JSON.parse(rawPermissions);
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
};

const PROTOTYPE_AUTH_KEY = 'isPrototypeAuth';
const PROTOTYPE_PROFILE_KEY = 'prototypeUserProfile';
const MANPRO_SESSION_PROFILE_KEY = 'manproSessionProfile';
const ADMIN_INFO_QUERY_KEY = ['auth', 'admin-info'] as const;

const readPrototypeProfile = (): UserProfile | null => {
  const rawProfile = localStorage.getItem(PROTOTYPE_PROFILE_KEY);

  if (!rawProfile) {
    return null;
  }

  try {
    return JSON.parse(rawProfile) as UserProfile;
  } catch {
    return null;
  }
};

const readSessionProfile = (): UserProfile | null => {
  const rawProfile = localStorage.getItem(MANPRO_SESSION_PROFILE_KEY);

  if (!rawProfile) {
    return null;
  }

  try {
    return JSON.parse(rawProfile) as UserProfile;
  } catch {
    return null;
  }
};

function App() {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('isLoggedIn') === 'true',
  );
  const [isAppLoading, setIsAppLoading] = useState(true);
  const isPrototypeAuthenticated =
    isAuthenticated && localStorage.getItem(PROTOTYPE_AUTH_KEY) === 'true';
  const persistedSessionProfile = readSessionProfile();
  const prototypeProfile = readPrototypeProfile();
  
  // Mencegah race-condition: Jangan panggil /admin/info jika sedang di proses SSO Callback
  const isSsoRoute = window.location.pathname.startsWith('/sso/callback');
  const shouldFetchUserInfo = isAuthenticated && !isPrototypeAuthenticated && !persistedSessionProfile && !isSsoRoute;

  const userInfoQuery = useQuery({
    queryKey: ADMIN_INFO_QUERY_KEY,
    queryFn: async () => {
      const response = await axios.get<ApiResponseEnvelope<UserProfile>>('/v1/admin/info');
      return response.data;
    },
    enabled: shouldFetchUserInfo,
    // Data profil dipakai lintas layout; staleTime membantu mencegah request ganda saat StrictMode/remount.
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const handleLogout = () => {
    setIsAuthenticated(false);
    setIsAppLoading(false);
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userRoles');
    localStorage.removeItem('token');
    localStorage.removeItem(PROTOTYPE_AUTH_KEY);
    localStorage.removeItem(PROTOTYPE_PROFILE_KEY);
    localStorage.removeItem(MANPRO_SESSION_PROFILE_KEY);
    queryClient.clear();
  };

  const userProfile = useMemo<UserProfile | null>(() => {
    if (isPrototypeAuthenticated) {
      return prototypeProfile;
    }

    if (userInfoQuery.data?.response_code === 'default_200') {
      return userInfoQuery.data.content;
    }

    return persistedSessionProfile;
  }, [isPrototypeAuthenticated, persistedSessionProfile, prototypeProfile, userInfoQuery.data]);

  const userRole = useMemo<PortalRole>(() => normalizePortalRole(userProfile?.user_type), [userProfile?.user_type]);

  const permissions = useMemo<string[]>(() => {
    const rolesFromProfile = userProfile?.roles?.map((role) => role.role_name) ?? [];

    return rolesFromProfile.length > 0 ? rolesFromProfile : parseStoredPermissions();
  }, [userProfile?.roles]);

  const facilitySubmodules = useMemo<FacilitySubmodule[]>(() => {
    return userProfile?.facility_submodules ?? [];
  }, [userProfile?.facility_submodules]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsAppLoading(false);
      return;
    }

    if (isPrototypeAuthenticated) {
      if (!prototypeProfile?.user_type) {
        handleLogout();
        setIsAppLoading(false);
        return;
      }

      setIsAppLoading(false);
      return;
    }

    if (shouldFetchUserInfo && userInfoQuery.isPending) {
      setIsAppLoading(true);
      return;
    }

    if (shouldFetchUserInfo && userInfoQuery.isError) {
      console.error('Failed to initialize app data:', userInfoQuery.error);
      handleLogout();
      setIsAppLoading(false);
      return;
    }

    if (shouldFetchUserInfo && userInfoQuery.data?.response_code === 'default_200') {
      const userData = userInfoQuery.data.content;
      const normalizedRole = normalizePortalRole(userData.user_type);

      if (!normalizedRole) {
        handleLogout();
        setIsAppLoading(false);
        return;
      }

      localStorage.setItem('userRole', normalizedRole);
      const roleNames = (userData.roles ?? []).map((role) => role.role_name);
      localStorage.setItem('userRoles', JSON.stringify(roleNames));
      setIsAppLoading(false);
      return;
    }

    if (persistedSessionProfile || userProfile) {
      setIsAppLoading(false);
      return;
    }

    setIsAppLoading(false);
  }, [
    isAuthenticated,
    isPrototypeAuthenticated,
    persistedSessionProfile,
    prototypeProfile,
    shouldFetchUserInfo,
    userInfoQuery.data,
    userInfoQuery.error,
    userInfoQuery.isError,
    userInfoQuery.isPending,
    userProfile,
  ]);

  const handleLogin = (role: string, options?: { prototype?: boolean }) => {
    const normalizedRole = normalizePortalRole(role);

    if (!normalizedRole) {
      return;
    }

    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userRole', normalizedRole);
    queryClient.removeQueries({ queryKey: ADMIN_INFO_QUERY_KEY });

    if (!options?.prototype) {
      const storedSessionProfile = readSessionProfile();

      if (storedSessionProfile) {
        const roleNames = (storedSessionProfile.roles ?? []).map((roleItem) => roleItem.role_name);
        localStorage.setItem('userRoles', JSON.stringify(roleNames));
      }
    }

    if (options?.prototype) {
      const prototypeRole = getPrototypeRoleByUserType(role);

      if (prototypeRole) {
        const prototypeProfileData = createPrototypeUserProfile(prototypeRole);
        const roleNames = prototypeProfileData.roles?.map((item) => item.role_name) ?? [];

        localStorage.setItem(PROTOTYPE_AUTH_KEY, 'true');
        localStorage.setItem(PROTOTYPE_PROFILE_KEY, JSON.stringify(prototypeProfileData));
        localStorage.setItem('userRoles', JSON.stringify(roleNames));
        localStorage.setItem('token', `prototype-token-${role}`);
      }
    }

    setIsAuthenticated(true);
    setIsAppLoading(true);
  };

  if (isAppLoading || (isAuthenticated && !userProfile)) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 to-slate-800">
        <div>
          <div className="mb-8 flex justify-center animate-pulse">
            <img
              src={logoApp}
              alt="Logo"
              className="w-24 h-24 object-contain"
            />
          </div>

          <div className="flex justify-center mb-6">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>

          <p className="text-white font-medium text-lg tracking-wide font-semibold">
            Memuat sistem...
          </p>

          <div className="w-48 h-1 bg-slate-700 rounded-full mx-auto overflow-hidden mt-6">
            <div className="h-full bg-blue-500 rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppZoomShell isAuthenticated={isAuthenticated}>
        <Routes>
          <Route path="/sso/callback" element={<SsoCallback onLogin={handleLogin} />} />
          <Route path="/invitations/accept/:token" element={<AcceptInvitationPage />} />
          <Route path="/guest/manpro/:token" element={<GuestMonitoring />} />

          <Route
            path="/login"
            element={
              !isAuthenticated ? (
                <Login onLogin={handleLogin} />
              ) : userRole === 'project_owner' ? (
                <Navigate to="/owner" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/owner"
            element={
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                userRole={userRole}
                userProfile={userProfile}
                allowedRoles={OWNER_ROLES}
              >
                <OwnerLayout onLogout={handleLogout} userProfile={userProfile} />
              </ProtectedRoute>
            }
          >
            <Route index element={<Beranda />} />
            <Route path="pasang" element={<PasangProyek userProfile={userProfile} />} />
            <Route path="pasang/:projectId/edit" element={<PasangProyek userProfile={userProfile} />} />
            <Route path="proyek-saya" element={<ProyekSaya />} />
            <Route path="proyek-saya/:projectId" element={<ProyekSayaDetail />} />
            <Route path="penawaran-masuk" element={<PenawaranMasuk />} />
            <Route path="penawaran-masuk/:bidId" element={<PenawaranDetail />} />
            <Route path="penawaran-masuk/:projectId/:bidId" element={<PenawaranDetail />} />
            <Route path="favorit" element={<Favorit />} />
            <Route path="vendor-tersimpan" element={<VendorTersimpan />} />
            <Route path="pesan" element={<Pesan userProfile={userProfile} />} />
            <Route path="notifikasi" element={<Notifikasi />} />
          </Route>

          <Route
            path="/"
            element={
              !isAuthenticated ? (
                <Landing />
              ) : (
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  userRole={userRole}
                  userProfile={userProfile}
                  allowedRoles={FACILITY_ROLES}
                >
                  <ProviderFavoriteProvider>
                    <FacilityManagementProvider>
                      <MainLayout
                        onLogout={handleLogout}
                        userRole={userRole}
                        permissions={permissions}
                        userProfile={userProfile}
                        facilitySubmodules={facilitySubmodules}
                      />
                    </FacilityManagementProvider>
                  </ProviderFavoriteProvider>
                </ProtectedRoute>
              )
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="project-management" element={<ProjectManagement />} />
            <Route path="provider-opportunities" element={<ProviderOpportunities />} />
            <Route path="provider-favorites" element={<FavoritProvider />} />
            <Route path="provider-opportunities/:opportunityId" element={<ProviderOpportunityDetail />} />
            <Route path="provider-opportunities/:opportunityId/bid" element={<ProviderBidCreate />} />
            <Route path="provider-bids" element={<ProviderBids />} />
            <Route path="provider-pesan" element={<ProviderPesan />} />
            <Route path="notifikasi" element={<Notifikasi />} />
            {/* <Route path="teknisi" element={<Teknisi />} /> */}
            {/* <Route path="cleaning" element={<Cleaning />} />
            <Route path="ac" element={<ACMonitoring />} />
            <Route path="waste" element={<WasteManagement />} />
            <Route path="procurement" element={<Procurement />} />
            <Route path="laundry" element={<Laundry />} /> */}

          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppZoomShell>
    </Router>
  );
}

export default App;

